import { Text, useColorMode } from '@chakra-ui/react'
import React, { useCallback, useEffect, useRef } from 'react'
import { useRecoilValue } from 'recoil'

import { gameState, userState } from './store'
import {
  clear,
  getMsPassed,
  growth,
  numberWithCommas,
  stepValues,
} from './utils'

export default function Canvas() {
  const { colorMode } = useColorMode()
  const game = useRecoilValue(gameState)
  const user = useRecoilValue(userState)

  const canvasRef = useRef()

  const draw = useCallback(
    ctx => {
      clear(ctx)

      let msPassed = getMsPassed(game.startDate, game.endDate)

      let result
      if (
        (game.state === 'WAITING' && msPassed < 0) ||
        game.state === 'LOADING'
      ) {
        result = 100
      } else if (game.state === 'CRASHED') {
        result = game.result
      } else {
        result = growth(msPassed)
      }

      const XAxisPlotMinValue = 10000
      const YAxisPlotMinValue = 200

      let YAxisPlotValue = YAxisPlotMinValue
      let XAxisPlotValue = XAxisPlotMinValue

      if (msPassed > XAxisPlotMinValue) XAxisPlotValue = msPassed
      if (result > YAxisPlotMinValue) YAxisPlotValue = result

      YAxisPlotValue -= 100

      const plotWidth = ctx.canvas.width - 40
      const plotHeight = ctx.canvas.height - 20

      const widthIncrement = plotWidth / XAxisPlotValue
      const heightIncrement = plotHeight / YAxisPlotValue

      const xStart = ctx.canvas.width - plotWidth
      const yStart = ctx.canvas.height - plotHeight

      drawAxis()

      // TODO: result and msPassed could use same technique to define crashed state

      if (game.state === 'LOADING' || game.state === 'WAITING') {
        ctx.fillStyle = colorMode === 'light' ? 'black' : '#b0b3c1'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        const fontSize = Math.floor(
          ctx.canvas.width * (game.state === 'WAITING' ? 0.05 : 0.1)
        )
        ctx.font = `${fontSize}px Roboto`

        let text
        if (game.state === 'LOADING') {
          text = 'Loading...'
        } else {
          const seconds = Math.abs(msPassed / 1000).toFixed(1)
          text = `Next round in ${seconds}s`
        }

        ctx.fillText(text, ctx.canvas.width / 2, ctx.canvas.height / 2)

        return
      }

      // ! CREATE GRAPH

      if (game.state === 'CRASHED') {
        ctx.strokeStyle = '#E53E3E'
        ctx.fillStyle = '#E53E3E'
      } else if (user.currentBet) {
        ctx.strokeStyle = '#38A169'
        ctx.fillStyle = '#38A169'
      } else if (game.state === 'STARTED') {
        ctx.strokeStyle = colorMode === 'light' ? 'black' : '#b0b3c1'
        ctx.fillStyle = colorMode === 'light' ? 'black' : '#b0b3c1'
      }

      ctx.lineWidth = 4

      ctx.beginPath()

      for (let t = 0, i = 0; t <= msPassed; t += 100, i++) {
        const y = plotHeight - (growth(t) - 100) * heightIncrement
        const x = t * widthIncrement

        ctx.lineTo(x + xStart, y)
        if (i > 5000) {
          break
        }
      }

      ctx.stroke()

      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      if (game.state === 'CRASHED') {
        const fontSize = Math.floor(ctx.canvas.width * 0.1375)
        ctx.font = `${fontSize}px Roboto`
        const text = `${(result / 100).toFixed(2)}x`
        ctx.fillText(
          'Crashed',
          ctx.canvas.width / 2,
          ctx.canvas.height / 2 - fontSize / 2
        )
        ctx.fillText(
          `@ ${text}`,
          ctx.canvas.width / 2,
          ctx.canvas.height / 2 + fontSize / 2
        )
      } else {
        const fontSize = Math.floor(ctx.canvas.width * 0.175)
        ctx.font = `${fontSize}px Roboto`
        const text = `${(result / 100).toFixed(2)}x`

        ctx.fillText(text, ctx.canvas.width / 2, ctx.canvas.height / 2)
      }

      function drawAxis() {
        //Calculate Y Axis
        const payoutSeparation = stepValues(result)

        ctx.lineWidth = 1
        ctx.strokeStyle = colorMode === 'light' ? 'black' : '#b0b3c1'
        ctx.font = '10px Roboto'
        ctx.fillStyle = colorMode === 'light' ? 'black' : '#b0b3c1'
        ctx.textAlign = 'center'

        //Draw Y Axis Values
        const heightIncrement = plotHeight / YAxisPlotValue
        for (
          let payout = payoutSeparation, i = 0;
          payout < YAxisPlotValue;
          payout += payoutSeparation, i++
        ) {
          const y = plotHeight - payout * heightIncrement

          // Initial one so the values don't start at 0, but we don't need it over 10x
          let initialOne = 1
          if (payout >= 1000) {
            initialOne = 0
          }

          ctx.fillText(`${Math.floor(payout) / 100 + initialOne}x`, 20, y)

          ctx.beginPath()
          ctx.moveTo(xStart, y)
          ctx.lineTo(xStart + 5, y)
          ctx.stroke()

          if (i > 100) {
            break
          }
        }

        //Calculate X Axis
        const milisecondsSeparation = stepValues(XAxisPlotValue)
        const XAxisValuesSeparation =
          plotWidth / (XAxisPlotValue / milisecondsSeparation)

        //Draw X Axis Values
        for (
          let miliseconds = 0, counter = 0, i = 0;
          miliseconds < XAxisPlotValue;
          miliseconds += milisecondsSeparation, counter++, i++
        ) {
          const seconds = miliseconds / 1000
          const textWidth = ctx.measureText(seconds).width
          const x = counter * XAxisValuesSeparation + xStart
          ctx.fillText(seconds, x - textWidth / 2, plotHeight + 11)

          if (i > 100) {
            break
          }
        }

        //Draw background Axis
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(xStart, 0)
        ctx.lineTo(xStart, ctx.canvas.height - yStart)
        ctx.lineTo(ctx.canvas.width, ctx.canvas.height - yStart)
        ctx.stroke()
      }
    },
    [game, user, colorMode]
  )

  // Handling Canvas Drawing
  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')

    let animationFrameId

    function render() {
      draw(ctx)
      animationFrameId = requestAnimationFrame(render)
    }
    render()

    return () => cancelAnimationFrame(animationFrameId)
  }, [draw])

  function resize() {
    const canvas = canvasRef.current
    canvas.width = canvas.offsetWidth
    canvas.height = canvas.offsetHeight
  }

  //  Handling Canvas Responsiveness
  useEffect(() => {
    resize()

    window.addEventListener('resize', resize)
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        resize()
      }, 400)
    })

    return () => {
      window.removeEventListener('resize', resize)
      window.removeEventListener('orientationchange', resize)
    }
  }, [game.state])

  return (
    <>
      {game.state !== 'LOADING' && (
        <Text fontSize="xs" pos="absolute" left="16" zIndex="0">
          Max profit: {numberWithCommas(game.maxProfit)} XRP
        </Text>
      )}
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
        }}
      ></canvas>
    </>
  )
}
